import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: '' });
API.interceptors.request.use(cfg => {
  const t = localStorage.getItem('token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const OBD_TYPES = [
  { value: 'none',   label: 'Sem OBD',      color: 'bg-gray-100 text-gray-600',   icon: '⚫' },
  { value: 'elm327', label: 'ELM327 (OBD-II)', color: 'bg-blue-100 text-blue-700',  icon: '🔵' },
  { value: 'j1939',  label: 'J1939 (CAN Bus)', color: 'bg-green-100 text-green-700', icon: '🟢' },
];

const SEV_COLORS = { info: 'bg-blue-100 text-blue-800', warning: 'bg-yellow-100 text-yellow-800', error: 'bg-red-100 text-red-800', critical: 'bg-red-200 text-red-900' };

function OBDTypeBadge({ type }) {
  const t = OBD_TYPES.find(x => x.value === type) || OBD_TYPES[0];
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${t.color}`}>{t.icon} {t.label}</span>;
}

function Reading({ label, value, unit, icon }) {
  if (value == null) return null;
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 flex flex-col gap-1">
      <div className="text-xs text-gray-500">{icon} {label}</div>
      <div className="text-xl font-bold text-gray-800">{typeof value === 'number' ? value.toFixed(1) : value}<span className="text-sm font-normal text-gray-500 ml-1">{unit}</span></div>
    </div>
  );
}

export default function OBDPage() {
  const [fleet, setFleet]       = useState([]);
  const [selected, setSelected] = useState(null);
  const [latest, setLatest]     = useState(null);
  const [dtcs, setDTCs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [editConfig, setEditConfig] = useState(null);

  // Carrega config de toda a frota
  const loadFleet = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/api/tracking/obd/fleet-config');
      setFleet(r.data.data || []);
    } catch (e) {
      toast.error('Erro ao carregar frota');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFleet(); }, [loadFleet]);

  // Carrega dados do dispositivo selecionado
  const loadDevice = useCallback(async (imei) => {
    if (!imei) return;
    try {
      const [latR, dtcR] = await Promise.all([
        API.get(`/api/tracking/gps-devices/${imei}/obd-latest`),
        API.get(`/api/tracking/gps-devices/${imei}/dtcs?active_only=true`),
      ]);
      setLatest(latR.data.data);
      setDTCs(dtcR.data.data || []);
    } catch (e) {
      console.error('Erro ao carregar device OBD', e);
    }
  }, []);

  const selectDevice = (dev) => {
    setSelected(dev);
    setEditConfig({ obd_type: dev.obd_type || 'none', obd_interval: dev.obd_interval || 30, obd_enabled: dev.obd_enabled || false });
    loadDevice(dev.imei);
    setLatest(null);
    setDTCs([]);
  };

  // Salva config OBD
  const saveConfig = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await API.put(`/api/tracking/gps-devices/${selected.imei}/obd-config`, editConfig);
      toast.success(`Config OBD enviada via MQTT para ${selected.license_plate || selected.imei}`);
      loadFleet();
    } catch (e) {
      toast.error('Erro ao salvar config OBD');
    } finally {
      setSaving(false);
    }
  };

  // Limpa DTCs
  const clearDTCs = async () => {
    if (!selected || !window.confirm('Limpar todos os DTCs ativos deste veículo?')) return;
    try {
      await API.post(`/api/tracking/gps-devices/${selected.imei}/dtcs/clear`);
      toast.success('DTCs limpos!');
      setDTCs([]);
    } catch (e) {
      toast.error('Erro ao limpar DTCs');
    }
  };

  const r = latest;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Diagnóstico OBD — Frota</h1>
        <p className="text-gray-500 text-sm mt-1">Configure o tipo de módulo OBD por veículo e acompanhe os dados da central eletrônica</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista da frota */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-semibold text-gray-700">Frota ({fleet.length})</span>
              <button onClick={loadFleet} className="text-xs text-blue-600 hover:underline">Atualizar</button>
            </div>
            {loading ? (
              <div className="p-8 text-center text-gray-400">Carregando...</div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                {fleet.map(dev => (
                  <div
                    key={dev.imei}
                    onClick={() => selectDevice(dev)}
                    className={`p-3 cursor-pointer hover:bg-blue-50 transition-colors ${selected?.imei === dev.imei ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm text-gray-800">{dev.license_plate || dev.imei}</span>
                      <OBDTypeBadge type={dev.obd_type} />
                    </div>
                    <div className="text-xs text-gray-500">{dev.brand} {dev.model} {dev.year}</div>
                    {dev.obd_enabled && (
                      <div className="text-xs text-green-600 mt-0.5">● OBD ativo — a cada {dev.obd_interval}s</div>
                    )}
                  </div>
                ))}
                {fleet.length === 0 && (
                  <div className="p-8 text-center text-gray-400 text-sm">Nenhum dispositivo GPS cadastrado</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Painel direito */}
        <div className="lg:col-span-2 space-y-5">
          {!selected ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
              <div className="text-4xl mb-3">🔧</div>
              <div>Selecione um veículo para configurar o OBD</div>
            </div>
          ) : (
            <>
              {/* Header veículo */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">{selected.license_plate || selected.imei}</h2>
                    <p className="text-sm text-gray-500">{selected.brand} {selected.model} {selected.year} · IMEI: {selected.imei}</p>
                  </div>
                  <OBDTypeBadge type={selected.obd_type} />
                </div>
              </div>

              {/* Config OBD */}
              {editConfig && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-700 mb-4">Configuração do Módulo OBD</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de módulo</label>
                      <select
                        value={editConfig.obd_type}
                        onChange={e => setEditConfig(c => ({ ...c, obd_type: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        {OBD_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Intervalo de leitura</label>
                      <select
                        value={editConfig.obd_interval}
                        onChange={e => setEditConfig(c => ({ ...c, obd_interval: parseInt(e.target.value) }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        {[10, 15, 30, 60, 120, 300].map(s => <option key={s} value={s}>{s}s</option>)}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editConfig.obd_enabled}
                          onChange={e => setEditConfig(c => ({ ...c, obd_enabled: e.target.checked }))}
                          className="w-4 h-4 rounded text-blue-600"
                        />
                        <span className="text-sm text-gray-700">Habilitar OBD</span>
                      </label>
                    </div>
                  </div>

                  {/* Info tipo */}
                  {editConfig.obd_type !== 'none' && (
                    <div className={`rounded-lg p-3 text-sm mb-4 ${editConfig.obd_type === 'j1939' ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'}`}>
                      {editConfig.obd_type === 'j1939' ? (
                        <><strong>J1939 (MCP2515):</strong> Dados completos via CAN Bus — RPM, temperatura, pressão turbo, pressão óleo, horas motor, velocidade, DTCs proprietários. Requer módulo MCP2515 conectado via SPI (GPIO 18/19/23/33).</>
                      ) : (
                        <><strong>ELM327:</strong> Dados OBD-II padrão — RPM, temperatura do motor, velocidade, carga, nível de combustível, DTCs. Requer módulo ELM327 UART conectado em GPIO 16 (RX) e 17 (TX).</>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={saveConfig}
                      disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                    >
                      {saving ? 'Enviando...' : '📡 Salvar e enviar ao firmware'}
                    </button>
                    <button
                      onClick={() => loadDevice(selected.imei)}
                      className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      🔄 Atualizar dados
                    </button>
                  </div>
                </div>
              )}

              {/* Última leitura OBD */}
              {r && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-700">Última Leitura OBD</h3>
                    <div className="flex items-center gap-2">
                      <OBDTypeBadge type={r.obd_type} />
                      <span className="text-xs text-gray-400">{r.recorded_at ? new Date(r.recorded_at).toLocaleString('pt-BR') : ''}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    <Reading label="RPM"               value={r.rpm}               unit="rpm"  icon="⚙️" />
                    <Reading label="Temp. Motor"       value={r.engine_temp}       unit="°C"   icon="🌡️" />
                    <Reading label="Temp. Óleo"        value={r.oil_temp}          unit="°C"   icon="🛢️" />
                    <Reading label="Pressão Óleo"      value={r.oil_pressure}      unit="kPa"  icon="💧" />
                    <Reading label="Pressão Turbo"     value={r.turbo_pressure}    unit="bar"  icon="💨" />
                    <Reading label="Temp. Câmbio"      value={r.transmission_temp} unit="°C"   icon="🔩" />
                    <Reading label="Pressão Ar"        value={r.air_pressure}      unit="bar"  icon="🌬️" />
                    <Reading label="Horas Motor"       value={r.engine_hours}      unit="h"    icon="⏱️" />
                    <Reading label="Nível Combustível" value={r.fuel_level}        unit="%"    icon="⛽" />
                    <Reading label="Pressão Combustível" value={r.fuel_pressure}   unit="kPa"  icon="🔧" />
                    <Reading label="Velocidade"        value={r.vehicle_speed}     unit="km/h" icon="🚛" />
                    <Reading label="Carga Motor"       value={r.engine_load}       unit="%"    icon="📊" />
                    <Reading label="Acelerador"        value={r.throttle_position} unit="%"    icon="🦶" />
                    <Reading label="Temp. Admissão"    value={r.intake_temp}       unit="°C"   icon="🌀" />
                    <Reading label="Temp. Escape"      value={r.exhaust_temp}      unit="°C"   icon="💥" />
                    <Reading label="Tensão Bateria"    value={r.battery_voltage}   unit="V"    icon="🔋" />
                  </div>
                </div>
              )}

              {!r && selected.obd_type !== 'none' && (
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
                  Nenhuma leitura OBD recebida ainda. Verifique se o módulo está conectado e o firmware está atualizado (v1.2).
                </div>
              )}

              {/* DTCs */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-700">
                    Códigos de Erro (DTCs)
                    {dtcs.length > 0 && <span className="ml-2 bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">{dtcs.length} ativo{dtcs.length > 1 ? 's' : ''}</span>}
                  </h3>
                  {dtcs.length > 0 && (
                    <button onClick={clearDTCs} className="text-xs text-red-600 hover:underline">🗑️ Limpar DTCs</button>
                  )}
                </div>
                {dtcs.length === 0 ? (
                  <div className="text-center py-6 text-green-600 text-sm">✅ Nenhum código de erro ativo</div>
                ) : (
                  <div className="space-y-2">
                    {dtcs.map(dtc => (
                      <div key={dtc.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <span className={`text-xs px-2 py-0.5 rounded font-mono font-bold ${SEV_COLORS[dtc.severity] || SEV_COLORS.warning}`}>
                          {dtc.dtc_code}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-800">{dtc.dtc_description || 'Código detectado — consultar manual do veículo'}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{new Date(dtc.detected_at).toLocaleString('pt-BR')} · {dtc.obd_type}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
